import type { AssemblyType, IstaLevel, SalesOrderLineStatus, UpdateSalesOrderLineInput } from '@/services/salesOrders/types'
import { formatDim } from '@/lib/units'

export interface GridLineItem {
  id?: string
  tempKey: string
  customer_id?: string
  customer_code?: string
  customer_name?: string
  buyer_id?: string | null
  buyer_name?: string | null
  isCopiedCustomer?: boolean
  isCopiedBuyer?: boolean
  variant_id?: string
  variant_code?: string
  product_name?: string
  product_id?: string
  has_image?: boolean
  color_name?: string
  color_swatch_hex?: string | null
  w?: number | null
  d?: number | null
  h?: number | null
  dim_unit?: 'inch' | 'mm'
  assembly_type: AssemblyType | ''
  ista_level: IstaLevel | ''
  quantity: number
  customer_po?: string | null
  pcs_per_carton?: number | null
  packing_note?: string | null
  requested_ship_date?: string | null
  updated_at?: string
  line_status: SalesOrderLineStatus
  status_reason?: string | null
  demand_status?: 'NOT_PUSHED' | 'PUSHED'
  demand_batch_ids?: string[]
  note?: string | null
}

export interface ParsedPasteResult {
  lines: Array<{
    customer_code: string
    variant_code: string
    assembly_type: AssemblyType | ''
    ista_level: IstaLevel | ''
    quantity: number
    customer_po: string
    rawRow: string
  }>
  errors: Array<{
    row: number
    message: string
  }>
}

/**
 * Normalizes assembly text into standard code: 'assembled' | 'kd' | ''
 */
export function normalizeAssemblyType(text: string): AssemblyType | '' {
  const clean = text.trim().toLowerCase()
  if (['assembled', 'ráp chết', 'rap chet', 'rapchet'].includes(clean)) {
    return 'assembled'
  }
  if (['kd', 'tháo rời', 'thao roi', 'thaoroi'].includes(clean)) {
    return 'kd'
  }
  return ''
}

/**
 * Normalizes ISTA level text into standard code: '1A' | '3A' | ''
 */
export function normalizeIstaLevel(text: string): IstaLevel | '' {
  const clean = text.trim().toUpperCase()
  if (clean === '1A' || clean === 'ISTA 1A' || clean === 'ISTA1A') {
    return '1A'
  }
  if (clean === '3A' || clean === 'ISTA 3A' || clean === 'ISTA3A') {
    return '3A'
  }
  return ''
}

/**
 * Parses tab/newline delimited text copied from Excel or spreadsheet.
 * Expected columns: Khách hàng, Mã hàng, Ráp/Tháo, ISTA, Số lượng, PO khách
 */
export function parseExcelPastedLines(text: string): ParsedPasteResult {
  const rawRows = text
    .split(/\r?\n/)
    .map((r) => r.trim())
    .filter((r) => r.length > 0)

  const lines: ParsedPasteResult['lines'] = []
  const errors: ParsedPasteResult['errors'] = []

  rawRows.forEach((rowStr, idx) => {
    const rowNum = idx + 1
    const cols = rowStr.split('\t').map((c) => c.trim())

    const customerCode = cols[0] || ''
    const variantCode = cols[1] || ''
    const assemblyRaw = cols[2] || ''
    const istaRaw = cols[3] || ''
    const qtyRaw = cols[4] || ''
    const po = cols[5] || ''

    const assemblyType = normalizeAssemblyType(assemblyRaw)
    const istaLevel = normalizeIstaLevel(istaRaw)
    const parsedQty = parseInt(qtyRaw.replace(/[^\d-]/g, ''), 10)
    const qty = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 0

    const rowErrors: string[] = []
    if (!customerCode) rowErrors.push('Thiếu mã khách hàng')
    if (!variantCode) rowErrors.push('Thiếu mã hàng')
    if (!assemblyType) rowErrors.push(`Ráp/tháo không hợp lệ: "${assemblyRaw}"`)
    if (!istaLevel) rowErrors.push(`ISTA không hợp lệ: "${istaRaw}"`)
    if (qty < 1 || qty > 999999) rowErrors.push(`Số lượng phải từ 1 đến 999.999 (nhận "${qtyRaw}")`)

    if (rowErrors.length > 0) {
      errors.push({
        row: rowNum,
        message: rowErrors.join(', '),
      })
    }

    lines.push({
      customer_code: customerCode,
      variant_code: variantCode,
      assembly_type: assemblyType,
      ista_level: istaLevel,
      quantity: qty,
      customer_po: po,
      rawRow: rowStr,
    })
  })

  return { lines, errors }
}

export interface ValidationResult {
  valid: boolean
  errors: Record<string, string>
  missingFields: string[]
}

/**
 * Validates a single grid line.
 * Required fields: customer_id (or code), variant_id (or code), assembly_type, ista_level, quantity.
 */
export function validateGridLine(line: GridLineItem): ValidationResult {
  const errors: Record<string, string> = {}
  const missingFields: string[] = []

  if (!line.customer_id && !line.customer_code) {
    errors.customer_id = 'Vui lòng chọn khách hàng'
    missingFields.push('customer_id')
  }

  if (!line.variant_id && !line.variant_code) {
    errors.variant_code = 'Vui lòng chọn mã hàng'
    missingFields.push('variant_code')
  }

  if (!line.assembly_type || (line.assembly_type !== 'assembled' && line.assembly_type !== 'kd')) {
    errors.assembly_type = 'Bắt buộc chọn Ráp chết hoặc Tháo rời'
    missingFields.push('assembly_type')
  }

  if (!line.ista_level || (line.ista_level !== '1A' && line.ista_level !== '3A')) {
    errors.ista_level = 'Bắt buộc chọn ISTA 1A hoặc 3A'
    missingFields.push('ista_level')
  }

  const qty = Number(line.quantity)
  if (!Number.isFinite(qty) || qty < 1 || qty > 999999 || qty !== Math.trunc(qty)) {
    errors.quantity = 'Số lượng phải là số nguyên từ 1 đến 999.999'
    missingFields.push('quantity')
  }

  return {
    valid: missingFields.length === 0,
    errors,
    missingFields,
  }
}

/**
 * Translates grid line items to payload for PATCH /api/sales-orders/:id.
 * CRITICAL REQUIREMENTS:
 * 1. Preserves line `id` for existing lines.
 * 2. NEVER sends `line_status` in the PATCH payload! (Status transitions must only happen via dedicated status API).
 */
export function buildPatchLinesPayload(lines: GridLineItem[]): UpdateSalesOrderLineInput[] {
  return lines.map((line) => {
    const payload: UpdateSalesOrderLineInput = {
      variant_id: line.variant_id ?? '',
      customer_id: line.customer_id || undefined,
      buyer_id: line.buyer_id || null,
      assembly_type: (line.assembly_type || 'assembled') as AssemblyType,
      ista_level: (line.ista_level || '1A') as IstaLevel,
      quantity: Number(line.quantity) || 0,
      customer_po: line.customer_po ? line.customer_po.trim() : null,
      pcs_per_carton:
        line.pcs_per_carton != null && line.pcs_per_carton !== ('' as any)
          ? Number(line.pcs_per_carton)
          : null,
      packing_note: line.packing_note ? line.packing_note.trim() : null,
      requested_ship_date: line.requested_ship_date ? line.requested_ship_date.trim() : null,
      note: line.note ? line.note.trim() : null,
    }

    if (line.id) {
      payload.id = line.id
    }

    return payload
  })
}

/**
 * Creates a new empty grid line.
 * Copies customer and buyer from previous line if present, marking them with copied flags.
 * Does NOT copy assembly_type, ista_level, or quantity.
 */
export function createEmptyGridLine(previousLine?: GridLineItem): GridLineItem {
  const tempKey = `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

  const hasPrevCustomer = Boolean(previousLine?.customer_id || previousLine?.customer_code)

  return {
    tempKey,
    customer_id: hasPrevCustomer ? previousLine?.customer_id || '' : '',
    customer_code: hasPrevCustomer ? previousLine?.customer_code || '' : '',
    customer_name: hasPrevCustomer ? previousLine?.customer_name || '' : '',
    buyer_id: hasPrevCustomer ? previousLine?.buyer_id ?? null : null,
    buyer_name: hasPrevCustomer ? previousLine?.buyer_name ?? null : null,
    isCopiedCustomer: hasPrevCustomer,
    isCopiedBuyer: Boolean(hasPrevCustomer && previousLine?.buyer_id),
    variant_id: '',
    variant_code: '',
    product_name: '',
    product_id: '',
    has_image: false,
    color_name: '',
    color_swatch_hex: null,
    w: null,
    d: null,
    h: null,
    dim_unit: 'inch',
    assembly_type: '',
    ista_level: '',
    quantity: 1,
    customer_po: '',
    pcs_per_carton: null,
    packing_note: '',
    requested_ship_date: '',
    updated_at: '',
    line_status: 'draft',
    status_reason: null,
    note: '',
  }
}

/**
 * Formats a millimeter dimension value into clean string without unit suffix.
 * Inch display defaults to 1 decimal place.
 */
export function formatLineDim(
  val: number | null | undefined,
  unit: 'inch' | 'mm' = 'inch',
): string {
  return formatDim(val, unit, 1)
}
